import React, { useState, useEffect, Suspense, lazy } from "react";
import {
  Routes,
  Route,
  useNavigate,
  useLocation,
  Link,
  Navigate,
} from "react-router-dom";
import { ViewState, User, UserRole } from "./types";
import { Navbar } from "./components/Navbar";
import { Footer } from "./components/Footer";
import { CookieConsent } from "./components/CookieConsent";
import { ToastContainer, ToastMessage, ToastType } from "./components/Toast";
import { Biohazard, Loader2 } from "lucide-react";
import { supabase } from "./supabaseClient";
import { SupportModal } from './components/SupportModal';
import { VercelAnalyticsManager } from './components/VercelAnalyticsManager';
import { NotificationManager } from './components/NotificationManager';
import { BackgroundParticles } from './components/BackgroundParticles';
import { HomeView } from './components/HomeView';
import { usePageMeta } from './hooks/usePageMeta';
import { useNewContentToast } from './hooks/useNewContentToast';
import { useEnsureDailyContent } from './hooks/useEnsureDailyContent';
import { useTranslation } from 'react-i18next';

// Lazy-loaded components for code splitting
const MemeFeed = lazy(() => import("./components/MemeFeed").then(m => ({ default: m.MemeFeed })));
const Leaderboard = lazy(() => import("./components/Leaderboard").then(m => ({ default: m.Leaderboard })));
const GamesPage = lazy(() => import("./components/GamesPage").then(m => ({ default: m.GamesPage })));
const Profile = lazy(() => import("./components/Profile").then(m => ({ default: m.Profile })));
const Rules = lazy(() => import("./components/Rules").then(m => ({ default: m.Rules })));
const Contact = lazy(() => import("./components/Contact").then(m => ({ default: m.Contact })));
const NewsPage = lazy(() => import("./components/NewsPage").then(m => ({ default: m.NewsPage })));
const Skjetaperson = lazy(() => import("./components/Skjetaperson").then(m => ({ default: m.Skjetaperson })));
const ConfessionsFeed = lazy(() => import("./components/ConfessionsFeed").then(m => ({ default: m.ConfessionsFeed })));
const SkjetaWiki = lazy(() => import("./components/SkjetaWiki").then(m => ({ default: m.SkjetaWiki })));
const AdminPanel = lazy(() => import("./components/AdminPanel").then(m => ({ default: m.AdminPanel })));
const PrivacyPolicy = lazy(() => import("./components/PrivacyPolicy").then(m => ({ default: m.PrivacyPolicy })));
const TermsOfService = lazy(() => import("./components/TermsOfService").then(m => ({ default: m.TermsOfService })));

const App: React.FC = () => {
  const { t } = useTranslation('App');
  const navigate = useNavigate();
  const location = useLocation();
  usePageMeta();
  const [user, setUser] = useState<User | null>(null);
  const [isLoginLoading, setIsLoginLoading] = useState(false);
  const [toasts, setToasts] = useState<ToastMessage[]>([]);

  // Helper to adapt legacy setView calls
  const setView = (view: ViewState) => {
    const routeMap: Record<ViewState, string> = {
      [ViewState.HOME]: "/",
      [ViewState.NEWS]: "/news",
      [ViewState.MEMES]: "/memes",
      [ViewState.LEADERBOARD]: "/leaderboard",
      [ViewState.GAME]: "/games",
      [ViewState.ORACLE]: "/oracle",
      [ViewState.PROFILE]: "/profile",
      [ViewState.ADMIN]: "/admin",
      [ViewState.RULES]: "/rules",
      [ViewState.CONTACT]: "/contact",
      [ViewState.SKJETAPERSON]: "/skjetaperson",
      [ViewState.CONFESSIONS]: "/confessions",
      [ViewState.WIKI]: "/wiki",
    };
    navigate(routeMap[view] || "/");
  };

  const currentView =
    (Object.keys(ViewState).find((key) => {
      const view = ViewState[key as keyof typeof ViewState];
      const routeMap: Record<ViewState, string> = {
        [ViewState.HOME]: "/",
        [ViewState.NEWS]: "/news",
        [ViewState.MEMES]: "/memes",
        [ViewState.LEADERBOARD]: "/leaderboard",
        [ViewState.GAME]: "/games",
        [ViewState.ORACLE]: "/oracle",
        [ViewState.PROFILE]: "/profile",
        [ViewState.ADMIN]: "/admin",
        [ViewState.RULES]: "/rules",
        [ViewState.CONTACT]: "/contact",
        [ViewState.SKJETAPERSON]: "/skjetaperson",
        [ViewState.CONFESSIONS]: "/confessions",
        [ViewState.WIKI]: "/wiki",
      };
      return location.pathname === routeMap[view];
    }) as ViewState) || ViewState.HOME;

  // Toast Helper
  const addToast = (message: string, type: ToastType = "info") => {
    const id = Date.now().toString();
    setToasts((prev) => [...prev, { id, message, type }]);
  };

  const removeToast = (id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  };

  useNewContentToast(addToast);
  useEnsureDailyContent();

  // Supabase Auth Listener
  useEffect(() => {
    // Check if running in a popup (OAuth callback)
    if (window.opener && window.name === "supabase-auth-popup") {
      supabase.auth.getSession().then(({ data: { session } }) => {
        if (session) {
          window.close();
        }
      });

      const {
        data: { subscription },
      } = supabase.auth.onAuthStateChange((_event, session) => {
        if (session) {
          window.close();
        }
      });
      return () => subscription.unsubscribe();
    }

    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) mapSessionToUser(session);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session) {
        mapSessionToUser(session);
      } else {
        setUser(null);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const mapSessionToUser = async (session: any) => {
    const { user } = session;
    const userId = user.id;
    let userName =
      user.user_metadata?.full_name || user.email?.split("@")[0] || "Anonym";
    let userAvatar =
      user.user_metadata?.avatar_url ||
      `https://api.dicebear.com/7.x/avataaars/svg?seed=${userId}`;

    let role: UserRole = "user";

    try {
      const { data: profile } = await supabase
        .from("profiles")
        .select("role, username, avatar_url")
        .eq("id", userId)
        .single();

      if (profile) {
        role = profile.role as UserRole;
        if (profile.username) userName = profile.username;
        if (profile.avatar_url) userAvatar = profile.avatar_url;
      } else {
        const initialRole =
          user.email === "alexanderseljen@gmail.com" ? "admin" : "user";
        await supabase.from("profiles").insert({
          id: userId,
          username: userName,
          avatar_url: userAvatar,
          role: initialRole,
        });
        role = initialRole;
      }

      // Force admin for specific email if not already set
      if (user.email === "alexanderseljen@gmail.com" && role !== "admin") {
        await supabase
          .from("profiles")
          .update({ role: "admin" })
          .eq("id", userId);
        role = "admin";
      }
    } catch (e) {
      console.error("Profile fetch error", e);
    }

    setUser({
      id: userId,
      username: userName,
      points: 0,
      avatar: userAvatar,
      rank: 0,
      streak: 0,
      badges: [],
      role: role,
    });
  };

  const handleLogin = async (provider: "google" | "discord" = "google") => {
    setIsLoginLoading(true);
    try {
      const redirectUrl = window.location.origin;
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: provider,
        options: {
          redirectTo: redirectUrl,
          skipBrowserRedirect: true,
        },
      });

      if (error) throw error;

      if (data?.url) {
        const width = 500;
        const height = 600;
        const left = window.screen.width / 2 - width / 2;
        const top = window.screen.height / 2 - height / 2;

        const popup = window.open(
          data.url,
          "supabase-auth-popup",
          `width=${width},height=${height},left=${left},top=${top},popup=true`,
        );

        if (!popup) {
          addToast(
            t('popupBlocked'),
            "error",
          );
          setIsLoginLoading(false);
          return;
        }

        // Poll to see if popup is closed
        const timer = setInterval(() => {
          if (popup.closed) {
            clearInterval(timer);
            setIsLoginLoading(false);
          }
        }, 500);
      }
    } catch (error: any) {
      console.error("Login error:", error);
      addToast(t('loginError', { message: error.message }), "error");
      setIsLoginLoading(false);
    }
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate("/");
    addToast(t('loggedOut'), "info");
  };

  return (
    <div className="min-h-screen bg-skjeta-dark text-skjeta-text font-sans selection:bg-skjeta-green selection:text-black flex flex-col overflow-x-hidden relative">

      <BackgroundParticles />
      <VercelAnalyticsManager />
      <NotificationManager user={user} addToast={addToast} />
      <ToastContainer toasts={toasts} removeToast={removeToast} />
      <CookieConsent />
      <SupportModal />

      <header
        className="fixed left-0 top-0 w-full p-4 flex justify-between items-center bg-skjeta-dark/80 backdrop-blur-md z-40 md:hidden border-b border-white/5"
      >
        <Link
          to="/"
          className="flex items-center gap-2 focus:outline-none focus:ring-2 focus:ring-skjeta-green rounded"
        >
          <Biohazard className="text-skjeta-green" />
          <span className="font-black italic text-xl tracking-tighter">
            SKJETA
          </span>
        </Link>
        <div className="flex items-center gap-4">
          {!user ? (
            <button
              onClick={() => navigate("/profile")}
              className="text-xs font-bold bg-white/10 px-3 py-1.5 rounded-full text-white focus:outline-none focus:ring-2 focus:ring-skjeta-green"
            >
              {t('logIn')}
            </button>
          ) : (
            <button
              onClick={() => navigate("/profile")}
              className="w-8 h-8 rounded-full bg-skjeta-purple p-0.5 overflow-hidden focus:outline-none focus:ring-2 focus:ring-skjeta-green"
            >
              <img
                src={user.avatar}
                className="w-full h-full rounded-full object-cover"
                alt={t('profileImageAlt')}
              />
            </button>
          )}
        </div>
      </header>

      {isLoginLoading && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[100] flex flex-col items-center justify-center text-white">
          <Loader2 className="w-12 h-12 animate-spin text-skjeta-green mb-4" />
          <p className="font-bold text-xl animate-pulse">
            {t('redirectingToGoogle')}
          </p>
        </div>
      )}

      <main className="container mx-auto px-4 flex-grow max-w-5xl relative z-10 pt-28 md:pt-40">
        <Suspense fallback={
          <div className="flex flex-col items-center justify-center pt-32 pb-24 text-center px-4 relative z-10 text-white">
            <Loader2 className="w-12 h-12 animate-spin text-skjeta-green mb-4" />
            <p className="font-bold text-xl animate-pulse text-gray-400">{t('loading')}</p>
          </div>
        }>
          <Routes>
            <Route path="/" element={<HomeView />} />
            <Route path="/news" element={<NewsPage user={user} />} />
            <Route path="/news/:articleSlug" element={<NewsPage user={user} />} />
            <Route path="/skjetaperson" element={<Skjetaperson user={user} />} />
            <Route path="/wiki" element={<SkjetaWiki />} />
            <Route path="/confessions" element={<ConfessionsFeed user={user} />} />
            <Route
              path="/memes"
              element={
                <MemeFeed
                  user={user}
                  onLoginRequest={() => {
                    navigate("/profile");
                    addToast(t('common:loginRequired'), "info");
                  }}
                  addToast={addToast}
                />
              }
            />
            <Route path="/leaderboard" element={<Leaderboard />} />
            <Route path="/games" element={<GamesPage user={user} onLoginRequest={() => { navigate("/profile"); addToast(t('loginRequiredToPlay'), "info"); }} />} />
            <Route
              path="/oracle"
              element={<Navigate to="/games" replace />}
            />
            <Route
              path="/profile"
              element={
                <Profile
                  user={user}
                  onLogin={handleLogin}
                  onLogout={handleLogout}
                  onAdminClick={() => navigate("/admin")}
                />
              }
            />
            <Route path="/admin" element={<AdminPanel user={user} />} />
            <Route path="/rules" element={<Rules />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/terms" element={<TermsOfService />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
          </Routes>
        </Suspense>
      </main>

      <Footer />

      <Navbar
        currentView={currentView}
        setView={setView}
        user={user}
      />
    </div>
  );
};

export default App;
